import { nanoid } from 'nanoid';

import { AudioRecordingPartialResult, MediaRecorderType } from '../../../main/types/audio.types.ts';
import { getLocaleUtil } from '../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../main/utils/monitoring/sentry.util.ts';

import {
  BACKUP_EVALUATION_DELAY,
  SPEECH_SUPER_DEFAULT_AUDIO_PARAMS,
  SpeechSuperBooleanEnum,
  SpeechSuperCoreTypeEnum,
} from '../../constants/speechsuper.constants.ts';
import { GraderType, PronunciationGraderParametersInput } from '../../types/pronunciation-grading-parameters.types.ts';
import { SpeechSuperStartGradingOptions } from '../../types/speechsuper-grading.types.ts';
import {
  SpeechSuperRequestParams,
  SpeechSuperSentenceRequestParamsSchema,
  SpeechSuperWordRequestParamsSchema,
} from '../../types/speechsuper-request.types.ts';
import { SpeechSuperFinalAssessmentResponse } from '../../types/speechsuper-response.types.ts';

import { getSpeechSuperConnectionManager } from './speechsuper-connection-manager.util.ts';
import { SpeechSuperConnection } from './speechsuper-connection.ts';

/**
 * Utility class to handle SpeechSuper grading.
 * Each instance represents a single grading session.
 *
 * Lifecycle:
 * 1. Initialize: Set up grading parameters and get connection
 * 2. Start: Begin the grading process
 * 3. Send Audio: Stream audio data for grading
 * 4. Stop: Get results and clean up
 *
 * Note: Not a singleton to allow multiple parallel grading sessions.
 */
export class SpeechSuperGradingUtil {
  // Grading state
  private connection: SpeechSuperConnection | null = null;
  private backupConnection: SpeechSuperConnection | null = null;
  private backupConnectionAbortController: AbortController | null = null;
  private isInitialized = false;
  private isStopped = false;
  private options: PronunciationGraderParametersInput | null = null;
  private audioData: AudioRecordingPartialResult[] = [];

  /**
   * Initializes the grader with the given parameters.
   * Must be called before starting grading.
   * @param options - Configuration options for the grading session
   */
  initialize(options: PronunciationGraderParametersInput): void {
    this.options = options;
    const coreType = this.mapGraderTypeToCoreType(options.graderType);
    this.connection = getSpeechSuperConnectionManager().getConnection(coreType);
    this.isInitialized = true;
    this.isStopped = false;
  }

  /**
   * Starts the grading process.
   * Must be called after initialize and before sending audio data.
   * @throws Error if not initialized
   */
  async startGrading(options?: SpeechSuperStartGradingOptions): Promise<void> {
    if (!this.isInitialized || !this.connection || !this.options) {
      throw new Error(getLocaleUtil().select('learn:grader_not_initialized'));
    }

    this.audioData = [];

    const request = this.getRequest();
    await this.connection.startAssessment(request, SPEECH_SUPER_DEFAULT_AUDIO_PARAMS, options?.callbacks);
  }

  private getRequest(): SpeechSuperRequestParams {
    if (!this.options) {
      throw new Error(getLocaleUtil().select('learn:grader_not_initialized'));
    }
    const coreType = this.mapGraderTypeToCoreType(this.options.graderType);

    if (coreType === SpeechSuperCoreTypeEnum.SENTENCE) {
      return SpeechSuperSentenceRequestParamsSchema.parse({
        tokenId: nanoid(),
        coreType,
        refText: this.options.refText,
        realtime_feedback: SpeechSuperBooleanEnum.TRUE,
        phoneme_output: SpeechSuperBooleanEnum.FALSE,
      });
    }
    return SpeechSuperWordRequestParamsSchema.parse({
      tokenId: nanoid(),
      coreType,
      refText: this.options.refText,
    });
  }

  /**
   * Sends audio data to be graded.
   * Must be called after startGrading.
   * @param audioData - The audio data to grade
   * @throws Error if not initialized or not started
   */
  async sendAudioData(audioData: AudioRecordingPartialResult): Promise<void> {
    if (!this.isInitialized || !this.connection) {
      throw new Error(getLocaleUtil().select('learn:grader_not_initialized_audio'));
    }
    if (this.isStopped) {
      // If the grading is stopped, we don't want to send any more audio data
      return;
    }

    if (audioData.mediaRecorderType === MediaRecorderType.GRADING) {
      this.audioData.push(audioData);
      await this.connection.sendAudio(audioData);
    }
  }

  /**
   * Stops grading and returns the results.
   * Automatically releases the connection back to the pool.
   * @returns The grading results
   * @throws Error if not initialized
   */
  async stopGrading(): Promise<SpeechSuperFinalAssessmentResponse | null> {
    if (!this.isInitialized || !this.connection || this.isStopped || !this.options) {
      throw new Error(getLocaleUtil().select('learn:grader_not_initialized_stop'));
    }

    try {
      this.isStopped = true;
      this.backupConnectionAbortController = new AbortController();

      const mainConnectionPromise = this.connection.stopAndGetResults();
      const backupConnectionPromise = this.createBackupConnectionPromise();

      const result = await Promise.race([mainConnectionPromise, backupConnectionPromise]);

      this.backupConnectionAbortController.abort();

      return result;
    } finally {
      // Connection is automatically released by stopAndGetResults
      this.connection = null;
      this.backupConnection = null;
      this.backupConnectionAbortController = null;
      this.isInitialized = false;
      this.options = null;
    }
  }

  /**
   * Aborts the current grading session and releases resources.
   */
  async abort(): Promise<void> {
    this.isStopped = true;

    if (this.connection) {
      await this.connection.stopAndGetResults();
      this.connection = null;
    }

    if (this.backupConnection) {
      await this.backupConnection.stopAndGetResults();
      this.backupConnection = null;
    }

    this.isInitialized = false;
    this.options = null;
  }

  private mapGraderTypeToCoreType(graderType: GraderType): SpeechSuperCoreTypeEnum {
    switch (graderType) {
      case GraderType.LETTER_NAME:
      case GraderType.PHONEME:
      case GraderType.WORD:
        return SpeechSuperCoreTypeEnum.WORD;
      case GraderType.SENTENCE:
        return SpeechSuperCoreTypeEnum.SENTENCE;
      default:
        throw new Error(getLocaleUtil().select('learn:unsupported_grader_type', { type: graderType }));
    }
  }

  private async createBackupConnectionPromise(): Promise<SpeechSuperFinalAssessmentResponse | null> {
    return new Promise<SpeechSuperFinalAssessmentResponse | null>((resolve) => {
      const timer = setTimeout(async () => {
        if (this.backupConnectionAbortController?.signal.aborted) {
          resolve(null);
          return;
        }

        const coreType = this.mapGraderTypeToCoreType(this.options!.graderType);
        const backupResult = await this.startBackupConnectionAndGetResults(coreType);
        resolve(backupResult);
      }, BACKUP_EVALUATION_DELAY);

      this.backupConnectionAbortController?.signal.addEventListener('abort', () => {
        clearTimeout(timer);
        resolve(null);
      });
    });
  }

  private async startBackupConnectionAndGetResults(
    coreType: SpeechSuperCoreTypeEnum
  ): Promise<SpeechSuperFinalAssessmentResponse | null> {
    this.backupConnection = getSpeechSuperConnectionManager().getConnection(coreType);

    try {
      const request = this.getRequest();
      await this.backupConnection.startAssessment(request, SPEECH_SUPER_DEFAULT_AUDIO_PARAMS);

      for (const audioData of this.audioData) {
        await this.backupConnection.sendAudio(audioData);
      }

      return await this.backupConnection.stopAndGetResults();
    } catch (error) {
      console.error('Backup connection failed:', error);
      getSentryUtil().trackError(error as Error, {
        connectionId: this.backupConnection?.id,
        coreType: this.backupConnection?.coreType,
      });
      return null;
    }
  }
}
